import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';

import { tokenStorage } from '../auth/tokenStorage';
import type { AuthResponse, ProblemDetails } from './types';

/**
 * Клиент API. Все данные ходят через /api — тот же контракт будет
 * у будущего мобильного клиента.
 */
export const apiClient = axios.create({
  baseURL: '/api',
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

/** Отдельный клиент для обновления токена: иначе перехватчик зациклится сам на себе. */
const refreshClient = axios.create({ baseURL: '/api', timeout: 15_000 });

let onUnauthorized: (() => void) | null = null;

/** Вызывается из AuthContext, чтобы сбросить состояние при протухшей сессии. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

apiClient.interceptors.request.use((config) => {
  const token = tokenStorage.getAccess();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Пока идёт обновление токена, параллельные запросы не должны запускать
 * его повторно: сервер ротирует refresh-токен, и второй запрос предъявил бы
 * уже потраченный, что выглядит как компрометация и отзывает всю цепочку.
 */
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefresh();
  if (!refreshToken) {
    throw new Error('Нет refresh-токена');
  }

  const { data } = await refreshClient.post<AuthResponse>('/auth/refresh', { refreshToken });
  tokenStorage.save(data.accessToken, data.refreshToken);
  return data.accessToken;
}

interface RetriableConfig extends AxiosRequestConfig {
  _retried?: boolean;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;

    const shouldRefresh =
      error.response?.status === 401 &&
      config !== undefined &&
      !config._retried &&
      tokenStorage.getRefresh() !== null;

    if (!shouldRefresh) {
      return Promise.reject(error);
    }

    config._retried = true;

    let accessToken: string;

    try {
      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });

      accessToken = await refreshPromise;
    } catch (refreshError) {
      // Сессию закрываем, только когда сервер прямо сказал, что токен больше
      // не годен. Обрыв сети или лежащий сервер — не повод спрашивать пароль
      // заново: токен бессрочный и со следующей попытки сработает.
      if (isUnauthorized(refreshError)) {
        tokenStorage.clear();
        onUnauthorized?.();
      }

      return Promise.reject(error);
    }

    config.headers = { ...config.headers, Authorization: `Bearer ${accessToken}` };

    // Повтор запроса уже вне try: его собственная ошибка к сессии отношения
    // не имеет, и гасить из-за неё вход нельзя.
    return apiClient.request(config);
  },
);

/** Сервер отказал именно в доступе, а не «не доехал». */
export function isUnauthorized(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}

/** Приводит ошибку axios к тексту, который можно показать пользователю. */
export function errorMessage(error: unknown, fallback = 'Не удалось выполнить операцию.'): string {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }

  const problem = error.response?.data as ProblemDetails | undefined;

  // У ошибок валидации осмысленный текст лежит в errors, а title — общий.
  const firstFieldError = problem?.errors
    ? Object.values(problem.errors).flat().find(Boolean)
    : undefined;

  if (firstFieldError) {
    return firstFieldError;
  }

  if (problem?.detail) {
    return problem.detail;
  }

  if (problem?.title) {
    return problem.title;
  }

  if (error.response === undefined) {
    return 'Сервер недоступен. Проверьте подключение к сети.';
  }

  return fallback;
}
